import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

export type EnvVar = { name: string; value: string };

/**
 * Reusable controlled editor for an array of `{ name, value }` env var pairs.
 * Renders only the rows and add/remove controls — callers provide their own
 * section header/label.
 */
export const EnvVarsEditor = ({
  value,
  onChange,
}: {
  value: EnvVar[];
  onChange: (next: EnvVar[]) => void;
}) => (
  <>
    {value.map((kv, i) => {
      const nameError = !kv.name && !!kv.value;
      const patch = (p: Partial<EnvVar>) =>
        onChange(value.map((v, j) => (j === i ? { ...v, ...p } : v)));
      return (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <TextField
            label="Name"
            value={kv.name}
            onChange={e => patch({ name: e.target.value })}
            margin="dense"
            variant="outlined"
            error={nameError}
            helperText={nameError ? 'Name is required when value is set' : undefined}
            style={{ flex: 1 }}
          />
          <TextField
            label="Value"
            value={kv.value}
            onChange={e => patch({ value: e.target.value })}
            margin="dense"
            variant="outlined"
            style={{ flex: 1 }}
          />
          <Button
            size="small"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            style={{ marginTop: 8 }}
          >
            Remove
          </Button>
        </div>
      );
    })}
    <Button
      size="small"
      variant="outlined"
      onClick={() => onChange([...value, { name: '', value: '' }])}
      style={{ marginTop: 8 }}
    >
      Add Environment Variable
    </Button>
  </>
);

/**
 * Scaffolder field wrapper around EnvVarsEditor.
 * Usage: `ui:field: EnvVars` on a field of type `array`.
 */
export const EnvVarsField = ({
  formData,
  onChange,
  schema,
}: FieldExtensionComponentProps<EnvVar[]>) => (
  <>
    {schema.description && <FormHelperText>{schema.description}</FormHelperText>}
    <EnvVarsEditor value={formData ?? []} onChange={onChange} />
  </>
);
